import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from "@angular/forms";
import { Router } from "@angular/router";
import { combineLatestWith, Subject, takeUntil } from "rxjs";

import { AuthenticationService } from "../../authentication/services/authentication.service";
import { StorageService } from "../../shared/storage/storage.service";
import { Profile, ProfileService } from "../../users/services/profile.service";
import { Organization } from "../models/organizations.entity";
import { OrganizationRepository } from "../repositories/organization.repository";
import { OrganizationStoreService } from "../services/organization.store.service";

@Component({
  selector: 'app-switch-profile',
  templateUrl: './switch-profile.component.html',
  styleUrls: [ './switch-profile.component.scss' ]
})
export class SwitchProfileComponent implements OnInit, OnDestroy {
  public profile!: Profile;

  public organizations!: Organization[];

  public organizationsFiltered: Organization[] = [];

  public organizationSelected?: Organization;

  public searchForm = new FormControl<string>('');

  private readonly destroy$ = new Subject<void>();

  get hasPortfolioAccess(): boolean {
    return (this.organizations?.length ?? 0) > 1
      || !!this.organizations?.some((organization) => !!organization.children?.length);
  }

  constructor(private readonly profileService: ProfileService,
              private readonly organizationStoreService: OrganizationStoreService,
              private readonly organizationRepository: OrganizationRepository,
              private readonly authenticationService: AuthenticationService,
              private readonly router: Router,
              private readonly storageService: StorageService) {
  }

  ngOnInit() {
    this.organizationStoreService.getObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe(organization => {
        this.organizationSelected = organization;
      });

    this.searchForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value) {
          this.filterOrganizations(value);
        } else {
          this.organizationsFiltered = this.organizations;
        }
      });

    this.profileService.getMyProfile()
      .pipe(combineLatestWith(this.organizationRepository.findAll(), this.organizationStoreService.getObservable()),
        takeUntil(this.destroy$))
      .subscribe(([ profile, organizations, organizationSelected ]) => {
        this.profile = profile;
        this.organizations = organizations;
        this.organizationsFiltered = organizations;
        this.organizationSelected = organizationSelected;
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  goToTalkerProfile() {
    // Clear organization context
    this.organizationStoreService.removeOrganization();

    // Call API to get redirect URL to new Talker app
    this.authenticationService.redirectToNewApp()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Redirect to new Talker app immediately, bypassing all Angular processing
          // Clear tokens first to prevent any guards from running
          this.storageService.logout();
          // Force immediate redirect outside Angular's zone
          setTimeout(() => {
            window.location.replace(response.redirectUrl);
          }, 0);
        },
        error: (error) => {
          console.error('Failed to redirect to new app:', error);
          // Fallback to old behavior if redirect fails
          this.router.navigate(['home']);
        }
      });
  }

  clickOnOrganization(organization: Organization) {
    this.router.navigate([ 'organizations', organization.id ]);

  }

  goToPortfolio(): void {
    this.router.navigate([ 'organizations', 'portfolio' ]);
  }

  filterOrganizations(searchString: string): void {
    const searchStringSplit = searchString.toLowerCase().split(' ').filter(s => s.length >= 1);
    this.organizationsFiltered = this.organizations.filter((organization) => {
      const organizationNameSplit = organization.name.toLowerCase().split(' ');
      return searchStringSplit.every(stringPart => organizationNameSplit.some(organizationPartName => organizationPartName.includes(stringPart)));
    });
  }
}
